import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { getUpdatedTime } from "config/ts-utils";
import { getBundles } from "containers/Bundles/Bundles.selectors";
import { SearchField } from "containers/ExecutionFilter/interfaces";
import { getProjects } from "containers/Projects/Projects.selectors";
import { ConfigType as DayjsConfigType } from "dayjs";
import { Bundle, Project, Record } from "graphql/generated/graphql";
import { History } from "history";
import { FeByType } from "config/config";
import { debounce, isEqual, isObject } from "lodash";
import { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { executionsPathFragment } from "utils/RouterManager/constants";
import {
    copySubPageQueryFlags,
    formatSearchParams,
    parseSearchParams,
    stripFilterFromSearch,
} from "utils/RouterManager/searchParams";
import { mapScreensToPaths } from "utils/RouterManager/utils";
import { getRelativeUrl } from "utils/service";
import {
    hasFilterQueryParams,
    importFilterQueryFromUrl,
    mergeQueryParamsWithStoredFilter,
    readStoredFilter,
    writeStoredFilter,
} from "./filterStorage";
import {
    getFilterString,
    parseFilterSafely,
    removeWildcards,
} from "utils/utils";
import { fieldOperationMapping } from "./constants";
import { StringValues } from "./interfaces";

export const useInitialLocationValue = () => {
    const projectList: Project[] = useSelector(getProjects);
    const bundleList: Bundle[] = useSelector(getBundles);
    const initialProjectOrBundleLocationValue = useMemo(
        () => ({
            projects: projectList.map((project) => project.id),
            bundles: bundleList.map((bundle) => bundle.id),
        }),
        [bundleList, projectList]
    );

    const initialLocationValue = useMemo(
        () => ({
            ...initialProjectOrBundleLocationValue,
            workspace: ["workspace"],
        }),
        [initialProjectOrBundleLocationValue]
    );

    return { initialProjectOrBundleLocationValue, initialLocationValue };
};

export const getFilterText = (value: string[], totalNumber = 0) => {
    if ((!value.length && !totalNumber) || value.length === totalNumber) {
        return "all";
    } else {
        return value.length;
    }
};

export const replaceFilterInQueryString = (
    history: History,
    filterStr: string,
    historyMethod: "push" | "replace" = "push"
) => {
    const params = parseSearchParams(history.location.search);
    params.filter = filterStr;
    history[historyMethod]({
        search: formatSearchParams(params),
    });
};

export const getValuesFromQuery = (queryParams: { [key: string]: string }) => {
    const findBooleanValueInQuery = (fieldName, defaultValue) =>
        queryParams[fieldName] ? queryParams[fieldName] === "true" : defaultValue;

    const findStringValueInQuery = (fieldName: string, defaultValue: string): string =>
        queryParams[fieldName] ? String(queryParams[fieldName]) : defaultValue;

    const findEmptyString = (fieldName) => {
        if (queryParams?.filter) {
            const filter = parseFilterSafely(queryParams.filter);

            return filter.and?.some((e: any) => {
                if (e.field === fieldName) {
                    return e.value === "";
                }
                return false;
            });
        }
    };

    const findValueInFilterQuery = (fieldName, op = "") => {
        let foundValue = "";
        let array: string[] = [];

        if (queryParams?.filter) {
            const filter = parseFilterSafely(queryParams.filter);

            filter.and?.forEach((e: any) => {
                if (e.field === fieldName && e.op === "in") {
                    array = [...e.value];
                } else {
                    if (e.or) {
                        e.or.forEach((o: StringValues) => {
                            if (o.field === fieldName) {
                                array.push(o.value.replaceAll("_", " "));
                            }
                        });
                    } else if (e.field === fieldName && e.op === op) {
                        foundValue = e.value;
                    }
                }
            });
        }

        return { array, foundValue };
    };

    const findEncodedValueInFilterQuery = (fieldName) => {
        let foundValue = "";

        if (queryParams?.filter) {
            const filter = parseFilterSafely(queryParams.filter);

            filter.and?.forEach((e: any) => {
                if (e.field === fieldName) {
                    try {
                        foundValue = removeWildcards(e.value);
                    } catch (error) {
                        foundValue = e.value;
                    }
                }
            });
        }

        return foundValue;
    };

    const findLocation = (initialLocation) => {
        let location;
        if (queryParams?.filter) {
            const filter = parseFilterSafely(queryParams.filter);

            filter.and?.forEach((e: any) => {
                if (e.field === "location") {
                    location = e.value;
                }
            });
        }
        return location ?? initialLocation;
    };

    const getFieldName = (key: string) => {
        if (key.includes("modified_at")) {
            return "modified_at";
        } else if (key.includes("created_at")) {
            return "created_at";
        } else if (key === "no_parent_id") {
            return "parent_execution_id";
        } else {
            return key;
        }
    };

    const setKeyValue = (filters: any[], key: string, op: string, value: string) => {
        filters.push({
            field: getFieldName(key),
            op,
            value,
        });
    };

    const skipNullValue = (filters: any[], key: string, op: string, value: string | null) => {
        if (value != null) {
            setKeyValue(filters, key, op, value);
        }
    };

    const addPrimitiveValueToQuery = (filters, key, op, value) => {
        if (key === "name" || key === "subjectOrData" || key === "recordLogName") {
            setKeyValue(filters, key, op, value);
        } else if (key === "no_parent_id") {
            const parentIdValue = value ? "" : null;
            skipNullValue(filters, key, op, parentIdValue);
        } else if (typeof value === "string") {
            setKeyValue(filters, key, op, value);
        }
    };

    return {
        findBooleanValueInQuery,
        findStringValueInQuery,
        findValueInFilterQuery,
        findEmptyString,
        getFieldName,
        findEncodedValueInFilterQuery,
        setKeyValue,
        skipNullValue,
        findLocation,
        addPrimitiveValueToQuery,
    };
};

export const getFilters = (values: {}, queryParams: { [key: string]: string }) => {
    const { addPrimitiveValueToQuery } = getValuesFromQuery(queryParams);
    const filters: Array<any> = [];

    for (const key in values) {
        const op = fieldOperationMapping[key];
        const value = values[key];

        if (Array.isArray(values[key])) {
            if (
                value.length ||
                key === "resource_type" ||
                key === "identity_type" ||
                key === "recordLogType" ||
                key === "status" ||
                key === "execution_type" ||
                key === "level" ||
                key === "project_id"
            ) {
                filters.push({
                    field: key,
                    op,
                    value,
                });
            }
        } else if (isObject(values[key])) {
            filters.push({
                field: key,
                op,
                value,
            });
        } else if (
            value &&
            key !== "include_deleted" &&
            key !== "hide_deleted" &&
            key !== "order" &&
            key !== "tab"
        ) {
            addPrimitiveValueToQuery(filters, key, op, value);
        }
    }
    return filters;
};

const buildSearchParamsObject = (params: {
    filter?: any;
    customFilter?: any;
    include_deleted?: boolean;
    hide_deleted?: boolean;
    tab?: string;
    id?: string;
    name?: string;
    [key: string]: any;
}) => {
    const { filter, customFilter, include_deleted, hide_deleted } = params;

    const result: { [key: string]: string | undefined } = {};
    const getFilterStr = getFilterString(filter);
    const getCustomFilterStr =
        Object.keys(customFilter || {}).length > 0 ? JSON.stringify(customFilter) : "";
    const includeDeleted = include_deleted ?? false;
    const hideDeleted = hide_deleted ?? true;

    if (params.id !== undefined || params.name !== undefined) {
        const param = params.id ? "id" : "name";
        result[param] = params[param] ?? "";
    }

    for (const key in params) {
        if (params[key] !== undefined && key !== "id" && key !== "name") {
            switch (key) {
                case "filter":
                    result.filter = getFilterStr;
                    break;
                case "customFilter":
                    if (getCustomFilterStr) {
                        result.customFilter = getCustomFilterStr;
                    }
                    break;
                case "include_deleted":
                    result.include_deleted = `${includeDeleted}`;
                    break;
                case "hide_deleted":
                    result.hide_deleted = `${hideDeleted}`;
                    break;
                default:
                    result[key] = params[key];
            }
        }
    }

    return result;
};

export const buildSearchParamsString = (params: {
    filter?: any;
    customFilter?: any;
    include_deleted?: boolean;
    hide_deleted?: boolean;
    tab?: string;
    id?: string;
    name?: string;
}) => formatSearchParams(buildSearchParamsObject(params));

const buildStoredFilterState = (
    values: { [key: string]: any },
    queryParams: { [key: string]: string }
) => {
    const { customFilter, ...restValues } = values;
    const filter = getFilters(restValues, queryParams);
    const storedCustomFilter = (() => {
        if (!customFilter) {
            return undefined;
        }
        if (typeof customFilter === "string") {
            return customFilter.length > 0 ? customFilter : undefined;
        }
        return Object.keys(customFilter).length > 0 ? JSON.stringify(customFilter) : undefined;
    })();

    return {
        filter: filter.length ? getFilterString(filter) : undefined,
        customFilter: storedCustomFilter,
        include_deleted: values.include_deleted ?? false,
        hide_deleted: values.hide_deleted ?? true,
        tab: values.tab,
    };
};

export const useSetQueryString = (
    queryParams: { [key: string]: string },
    history: History,
    filterStorageKey?: string,
    // True while the container context (workspace/project/bundle id) is still resolving, so
    // `filterStorageKey` is only transiently undefined. Reported as "not initialized" so the
    // on-mount initial-filter write is withheld until the key is known -- otherwise the default
    // filter is pushed into the URL, then stripped once the key resolves, and the two writers
    // ping-pong on location.search (unbounded re-renders -> React #185).
    isContextPending = false
) => {
    const [filterRevision, setFilterRevision] = useState(0);
    const [isFilterInitialized, setIsFilterInitialized] = useState(!filterStorageKey);
    const storedFilter = useMemo(() => {
        void filterRevision;
        return filterStorageKey ? readStoredFilter(filterStorageKey) : null;
    }, [filterStorageKey, filterRevision]);
    const effectiveQueryParams = useMemo(() => {
        if (!filterStorageKey) {
            return queryParams;
        }
        // Deep link: URL filter params apply on this navigation before they are imported.
        if (hasFilterQueryParams(queryParams)) {
            return queryParams;
        }
        return mergeQueryParamsWithStoredFilter(queryParams, storedFilter);
    }, [filterStorageKey, queryParams, storedFilter]);

    useLayoutEffect(() => {
        if (!filterStorageKey) {
            setIsFilterInitialized(true);
            return;
        }
        const urlParams = parseSearchParams(history.location.search);
        if (importFilterQueryFromUrl(filterStorageKey, urlParams)) {
            setFilterRevision((revision) => revision + 1);
        }
        const cleanedSearch = stripFilterFromSearch(history.location.search);
        const currentSearch = history.location.search.startsWith("?")
            ? history.location.search.substring(1)
            : history.location.search;
        if (cleanedSearch !== currentSearch) {
            history.replace({
                pathname: history.location.pathname,
                search: cleanedSearch ? `?${cleanedSearch}` : "",
            });
        }
        setIsFilterInitialized(true);
    }, [filterStorageKey, history, history.location.search]);

    const getUidParams = useCallback((params) => {
        const param = params.id || params.name;
        return param ? { [params.id ? "id" : "name"]: param } : {};
    }, []);

    const persistStoredFilter = useCallback(
        (values: { [key: string]: any }) => {
            if (!filterStorageKey) {
                return;
            }
            writeStoredFilter(filterStorageKey, buildStoredFilterState(values, queryParams));
            setFilterRevision((revision) => revision + 1);
        },
        [filterStorageKey, queryParams]
    );

    const setCustomFilter = useCallback(
        (customFilter) => {
            if (filterStorageKey) {
                const current = readStoredFilter(filterStorageKey) ?? {};
                writeStoredFilter(filterStorageKey, {
                    ...current,
                    customFilter: Object.keys(customFilter).length
                        ? getFilterString(customFilter.and ?? customFilter)
                        : undefined,
                });
                setFilterRevision((revision) => revision + 1);
                return;
            }
            const params = parseSearchParams(history.location.search);
            if (Object.keys(customFilter).length) {
                params.customFilter = JSON.stringify(customFilter);
            } else {
                delete params.customFilter;
            }
            history.push({ search: formatSearchParams(params) });
        },
        [filterStorageKey, history]
    );

    const setQueryString = useCallback(
        (values: { [key: string]: any }) => {
            if (filterStorageKey) {
                persistStoredFilter(values);
                return;
            }

            const { customFilter, ...restValues } = values;

            const filter: Array<any> = getFilters(restValues, queryParams);
            const uidParams = getUidParams(queryParams);
            const params = buildSearchParamsObject({
                filter,
                customFilter,
                include_deleted: restValues.include_deleted,
                hide_deleted: restValues.hide_deleted,
                tab: restValues.tab,
                ...uidParams,
            });
            copySubPageQueryFlags(params, history.location.search);
            history.push({
                search: formatSearchParams(params),
            });
        },
        [filterStorageKey, getUidParams, history, persistStoredFilter, queryParams]
    );

    const getInitialQueryString = useCallback(
        (initialFilter) => {
            const { addPrimitiveValueToQuery } = getValuesFromQuery(effectiveQueryParams);
            const filters: any = [];

            for (const key in initialFilter) {
                const op = fieldOperationMapping[key];

                if (Array.isArray(initialFilter[key])) {
                    if (initialFilter[key].length) {
                        filters.push({
                            field: key,
                            op,
                            value: initialFilter[key],
                        });
                    }
                } else {
                    if (initialFilter[key]) {
                        addPrimitiveValueToQuery(filters, key, op, initialFilter[key]);
                    }
                }
            }

            const filterStr = filters.length ? getFilterString(filters) : null;

            return filterStr;
        },
        [effectiveQueryParams]
    );
    const setInitialQueryString = useCallback(
        (
            initialFilter,
            historyMethod = "push",
            additionalParams: { [key: string]: string } = {}
        ) => {
            if (filterStorageKey) {
                persistStoredFilter({ ...initialFilter, ...additionalParams });
                return;
            }

            const idParams = getUidParams(queryParams);
            const filter = getFilters(initialFilter, queryParams);

            const params = buildSearchParamsObject({
                filter,
                include_deleted: initialFilter.include_deleted,
                hide_deleted: initialFilter.hide_deleted,
                tab: initialFilter.tab,
                ...idParams,
                ...additionalParams,
            });
            copySubPageQueryFlags(params, history.location.search);

            history[historyMethod]({
                search: formatSearchParams(params),
            });
        },
        [filterStorageKey, getUidParams, history, persistStoredFilter, queryParams]
    );

    return {
        setInitialQueryString,
        setQueryString,
        getInitialQueryString,
        replaceFilterInQueryString,
        setCustomFilter,
        effectiveQueryParams,
        hasStoredFilter: Boolean(storedFilter?.filter),
        isFilterInitialized: isContextPending ? false : isFilterInitialized,
    };
};

const getNewCheckedValues = (newValue, isChecked, currentArray) => {
    if (isChecked) {
        if (!currentArray.includes(newValue)) {
            return [...currentArray, newValue];
        }
        return currentArray;
    }
    return currentArray.filter((id) => id !== newValue);
};

const convertDayJsObjToString = (dayJsValue: DayjsConfigType | string) => {
    if (!dayJsValue) {
        return null;
    }
    if (typeof dayJsValue === "string") {
        return dayJsValue;
    }
    return getUpdatedTime(dayJsValue, { include_utcoffset: true });
};

const getValuesWithDateTime = (values) => ({
    ...values,
    created_at_gt: convertDayJsObjToString(values.created_at_gt),
    created_at_lt: convertDayJsObjToString(values.created_at_lt),
});

export const submitFilterForm = ({
    prevValues,
    newValues,
    setQueryString,
    textFields,
    initialLocationValue = null,
}) => {
    const prevValuesWithDateTime = getValuesWithDateTime(prevValues);
    const newValuesWithDateTime = getValuesWithDateTime(newValues);

    const allValues = { ...prevValuesWithDateTime, ...newValuesWithDateTime };

    textFields.forEach((textField) => {
        if (
            prevValuesWithDateTime[textField]?.length &&
            !newValuesWithDateTime[textField]?.length
        ) {
            allValues[textField] = "";
        }
    });

    // to avoid passing location to the url as it can be too long
    if (initialLocationValue && isEqual(allValues.location, initialLocationValue)) {
        delete allValues.location;
    }

    setQueryString(allValues);
};

export const debouncedSubmit = debounce((submit) => submit(), 350);

export const onCheckboxValueChange =
    ({ form, identifier, currentValue, newValue }) =>
    (e) => {
        const newValues = getNewCheckedValues(newValue, e?.target?.checked, currentValue);
        form.change(identifier, newValues);
    };

export const useQueryParams = () => {
    const location = useLocation();
    const { search } = location;

    const queryParams = useMemo(() => {
        const query = new URLSearchParams(search);
        const params: { [key: string]: string } = {};
        query.forEach((value, key) => {
            try {
                params[key] = decodeURIComponent(value);
            } catch (error) {
                console.warn(`Failed to decode parameter '${key}':`, error);
                params[key] = value;
            }
        });
        return params;
    }, [search]);

    return { queryParams };
};

export const getLocationOptionsList = (locationList) =>
    locationList.map((location) => {
        const label = (
            <span className="projects-list-item">
                {location?.icon ? (
                    <span
                        className="projects-list-item__image"
                        style={{
                            backgroundImage: `url(${location?.icon})`,
                        }}
                    />
                ) : (
                    <Icon icon={IconCollection.folderOutline} />
                )}
                {location?.name}
            </span>
        );
        return {
            value: location.id,
            label,
        };
    });

export const executionFilterPerLocationLink = (
    locationType: "project" | "bundle",
    recordRef: string,
    by: FeByType = "name",
    _additionalFilterField?: { value: any; field: string; op: string }
) => {
    return `${getRelativeUrl(locationType, recordRef, by)}&${executionsPathFragment}`;
};

export const executionFilterPerProjectLink = (projectId: string) => {
    // dashboard widgets invoke this with the container's record UUID (recordId),
    // so the deep-link must resolve the scope by id, not name (the default).
    // Otherwise `/project?name=<uuid>` fails to resolve and the monitor falls
    // through to the unscoped workspace view with empty status/type filters.
    return executionFilterPerLocationLink("project", projectId, "id");
};
export const executionFilterPerBundleLink = (id: string) => {
    return executionFilterPerLocationLink("bundle", id, "id");
};
export const executionFilterPerBundleOrWorkspaceLink = (
    locationType: "bundle" | "workspace",
    locationId: string,
    workspaceId: string
) => {
    return mapScreensToPaths.executionFilter({
        id: locationType === "workspace" ? locationId : workspaceId,
    });
};

export const getWorkspaceExecutionFilterLink =
    (..._filterFields: SearchField[]) =>
    (resourceId) => {
        return mapScreensToPaths.executionFilter({
            id: resourceId,
        });
    };

export const getLocationQuery = (location) => {
    const innerOr: Array<Record<string, unknown>> = [];
    if (location.projects?.length) {
        innerOr.push({ field: "project_id", op: "in", value: location.projects });
    }
    if (location.bundles?.length) {
        innerOr.push({ field: "bundle_id", op: "in", value: location.bundles });
    }
    if (!location.workspace || location.workspace.includes("workspace")) {
        innerOr.push({
            and: [
                { field: "project_id", op: "unset", value: null },
                { field: "bundle_id", op: "unset", value: null },
            ],
        });
    }
    return { or: [{ or: innerOr }] };
};

export const getLocationNameArray = (
    currentLocationIdValue: string[],
    locationList: Bundle[] | Project[]
) => {
    return currentLocationIdValue?.length > 0
        ? currentLocationIdValue.map(
              (locationId) =>
                  (locationList as Record[]).find((item) => item.id === locationId)?.name ?? ""
          )
        : [];
};
